import React, { useState, useEffect } from "react";
import { axiosInstance, config } from "../../Axios/AxiosInstance";
import toast from "react-hot-toast";
import AdminLayout from "./Common/AdminLayout";
import LoadingSkeleton from "./Common/LoadingSkeleton";
import ReactQuill from "react-quill";
import { FaSave, FaFileAlt } from "react-icons/fa";
import "../../asset/new/css/admin-modern.css";

const PrivacyEdit = () => {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch the existing FAQ content on component mount
  useEffect(() => {
    const fetchFAQ = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/faq', config);
        setText(response?.data?.data?.[0]?.details || "");
      } catch (error) {
        toast.error("Failed to fetch Privacy Policy content.");
      } finally {
        setLoading(false);
      }
    };
    fetchFAQ();
    document.title = "Privacy Policy | Admin";
  }, []);

  // Handle change in the editor
  const handleChange = (value) => {
    setText(value);
  };

  // Handle submit for updating FAQ content
  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error("Privacy Policy content cannot be empty");
      return;
    }
    
    try {
      setSaving(true);
      await axiosInstance.post('/faq', { details: text }, config);
      toast.success("Privacy Policy updated successfully!");
    } catch (error) {
      toast.error("Failed to update Privacy Policy.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout
      title="Privacy Policy"
      subtitle="Edit website privacy policy and terms"
      actions={
        <button
          onClick={handleSubmit}
          className="admin-btn admin-btn-primary"
          disabled={saving || loading}
        >
          <FaSave className="me-2" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      }
    >
      {loading ? (
        <div className="admin-card">
          <div className="admin-card-body">
            <LoadingSkeleton type="form" />
          </div>
        </div>
      ) : (
        <div className="admin-card">
          <div className="admin-card-body">
            <div className="d-flex align-items-center mb-4">
              <div
                className="me-3"
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "var(--radius-lg)",
                  background: "var(--admin-primary-light)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--admin-primary)",
                }}
              >
                <FaFileAlt size={24} />
              </div>
              <div>
                <h5 className="mb-1" style={{ fontSize: "1.25rem", fontWeight: 600 }}>
                  Privacy Policy Content
                </h5>
                <p className="admin-text-muted mb-0" style={{ fontSize: "0.875rem" }}>
                  Use the editor below to update your privacy policy
                </p>
              </div>
            </div>

            <div className="mb-3">
              <ReactQuill
                theme="snow"
                value={text}
                onChange={handleChange}
                style={{ 
                  height: "500px", 
                  backgroundColor: "white",
                  borderRadius: "var(--radius-md)",
                  marginBottom: "60px"
                }}
                modules={{
                  toolbar: [
                    [{ header: [1, 2, 3, 4, 5, 6, false] }],
                    ["bold", "italic", "underline", "strike"],
                    [{ list: "ordered" }, { list: "bullet" }],
                    [{ align: [] }],
                    ["link", "image"],
                    ["clean"],
                  ],
                }}
              />
            </div>

            <div className="d-flex gap-3 mt-3">
              <button
                onClick={handleSubmit}
                className="admin-btn admin-btn-primary"
                disabled={saving || !text.trim()}
              >
                <FaSave className="me-2" />
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="admin-btn admin-btn-secondary"
                disabled={saving}
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default PrivacyEdit;
